import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-950">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />

      <main>
        <article className="py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            {/* Header */}
            <header className="mb-10">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs text-accent-500 font-medium bg-accent-100/80 px-2.5 py-1 rounded-full hover:bg-accent-200 transition-colors"
                >
                  {article.category}
                </Link>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 bg-background-100 text-foreground-600 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 leading-tight">
                {article.title}
              </h1>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="font-medium text-foreground-700">{article.author}</span>
                <span>{article.publishDate}</span>
                <span>{article.readTime} 分钟阅读</span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 女性裸体艺术解析`}
                className="w-full object-cover"
                style={{ maxHeight: '480px', objectPosition: 'center' }}
              />
            </div>

            {/* Content */}
            <div
              className="prose-content text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Article Footer */}
            <div className="mt-14 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-3">
                <span className="text-sm font-medium text-foreground-700">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-sm px-3 py-1 bg-background-100 text-foreground-600 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-18 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300/60 transition-all duration-300"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <span className="text-xs text-accent-500 font-medium bg-accent-100/80 px-2 py-0.5 rounded-full">
                        {related.category}
                      </span>
                      <h3 className="mt-3 text-sm font-heading text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {related.title}
                      </h3>
                      <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
                        <span>{related.author}</span>
                        <span>{related.readTime} 分钟阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}